// Parti satır tablosu — Geçmiş Rapor Aktarımı (Task 13, tasarım review 1A + 6A + 15A).
//
// 1A: 5 kolon (Dosya · Müşteri · Yıl/Dönem · Framework/Paket · Durum sabit 160 px)
//     + her hücrenin 2. satırı, tablo `min-width: 960` + yatay kaydırma,
//     ≤ 1100 px'te satırlar KART düzenine geçer (matchMedia — yeni bağımlılık yok).
// 6A: satır sonunda 24×24 hayalet kaldır düğmesi; ✦ yalnız `suggested` alanlarda,
//     elle düzenleme `markRowTouched` ile ✦'yi kaldırır (çağıran uygular).
// 15A: 14 px altı tüm metin `--color-text-2`.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  PERIOD_LABELS,
  PERIOD_TYPES,
  REPORT_TYPE_LABELS,
  REPORT_TYPES,
  SECTOR_CODES,
  SECTOR_LABELS,
  type PeriodType,
  type PublishedFrameworkEntry,
  type ReportType,
  type SectorCode,
  type SectorPackageSummary,
} from "@mahir/shared";
import { searchClients } from "../../api/clients";
import { fillTemplate } from "../../lib/period-ui";
import { useMediaQuery } from "../../lib/use-media-query";
import { ClientPicker } from "../common/ClientPicker";
import { PICKER_DEBOUNCE_MS } from "../common/ClientPickerCore";
import {
  IMPORT_COPY,
  clientSearchTerm,
  exactClientMatch,
  matchSectorPackage,
  rowReadinessOf,
  sectorMismatchNote,
  similarClientOptions,
  type ClientOption,
  type ExistingImportKey,
  type ImportRowField,
  type ImportRowState,
} from "../../lib/report-import-ui";

const CARD_BREAKPOINT = "(max-width: 1100px)";
const SMALL = "0.78rem";

/** Uzun dosya adında ORTA kesme + tam ad `title` (1A). */
function middleEllipsis(name: string, max = 34): string {
  if (name.length <= max) return name;
  const head = Math.ceil((max - 1) / 2);
  const tail = Math.floor((max - 1) / 2);
  return `${name.slice(0, head)}…${name.slice(name.length - tail)}`;
}

const inputStyle: React.CSSProperties = {
  padding: "4px 6px",
  borderRadius: 6,
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  color: "var(--color-text)",
  fontSize: "0.82rem",
  maxWidth: "100%",
  textOverflow: "ellipsis",
};

function Star(): JSX.Element {
  return (
    <span
      title={IMPORT_COPY.suggestionMark}
      aria-label={IMPORT_COPY.suggestionMark}
      style={{ color: "var(--color-primary)", fontWeight: 600 }}
    >
      ✦
    </span>
  );
}

function ReadinessPill({
  row,
  existing,
}: {
  row: ImportRowState;
  existing: readonly ExistingImportKey[];
}): JSX.Element {
  const readiness = rowReadinessOf(row, existing);
  const tone =
    readiness === "hazır"
      ? {
          background: "color-mix(in srgb, var(--color-success) 12%, white)",
          color: "var(--color-text-2)",
          border: "1px solid transparent",
        }
      : {
          background: "var(--color-warning-bg)",
          color: "var(--color-warning-text)",
          border: "1px solid var(--color-warning-border)",
        };
  const label = readiness === "zaten var" ? IMPORT_COPY.existsInFramework : readiness;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: SMALL,
        fontWeight: 600,
        ...tone,
      }}
    >
      {label}
    </span>
  );
}

/** `eksik` satırında hangi alanların boş olduğunu söyler (8A PARTIAL sütunu). */
function missingNote(row: ImportRowState): string | null {
  const missing: string[] = [];
  if (!row.clientId) missing.push(IMPORT_COPY.colClient.toLocaleLowerCase("tr-TR"));
  if (!row.frameworkVersionId) missing.push(IMPORT_COPY.colFramework.toLocaleLowerCase("tr-TR"));
  if (!row.reportingYear) missing.push(IMPORT_COPY.colYear.toLocaleLowerCase("tr-TR"));
  if (row.files.length === 0) missing.push(IMPORT_COPY.colFile.toLocaleLowerCase("tr-TR"));
  return missing.length > 0 ? missing.join(", ") : null;
}

/** Satır içi müşteri oluşturma sonucu — hata editörde kırmızı satır olur. */
export interface CreateClientResult {
  ok: boolean;
  message?: string;
}

export interface ImportRowsTableProps {
  rows: ImportRowState[];
  /**
   * BİLİNEN müşteriler (ClientPicker işi, 2026-10-07): tam liste DEĞİL — seçilen / satır
   * içinde oluşturulan / id'den çözülen kayıtlar. Çip adı ve paket önerisinin sektörü
   * buradan okunur; arama sunucuda (`GET /clients?search=`).
   */
  clients: ClientOption[];
  /** Picker'dan seçilen kayıt — çağıran önbelleğe ekler (sektör paket önerisi için). */
  onClientKnown: (client: ClientOption) => void;
  frameworks: PublishedFrameworkEntry[];
  /** Framework sürümü → yayınlanmış sektör paketleri (çağıran yükler). */
  packagesByVersion: Record<string, SectorPackageSummary[]>;
  existing: readonly ExistingImportKey[];
  /** `clients.create` izni — yoksa "+ Yeni müşteri" seçeneği hiç render edilmez. */
  canCreateClient: boolean;
  onPatch: (id: string, patch: Partial<ImportRowState>, field?: ImportRowField) => void;
  onRemove: (id: string) => void;
  onRetryUpload: (id: string) => void;
  onCreateClient: (
    rowId: string,
    name: string,
    /** Sözlük etiketine indirgenmiş sektör (`clients.sector` serbest metni) ya da null. */
    sector: string | null,
  ) => Promise<CreateClientResult>;
}

export function ImportRowsTable(props: ImportRowsTableProps): JSX.Element {
  const asCards = useMediaQuery(CARD_BREAKPOINT);
  const { rows } = props;

  if (asCards) {
    return (
      <div className="import-rows flex flex-col gap-2">
        {rows.map((row) => (
          <RowCard key={row.id} row={row} {...props} />
        ))}
      </div>
    );
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", minWidth: 960, fontSize: "0.88rem" }}>
        <thead>
          <tr
            style={{
              background: "var(--color-bg)",
              color: "var(--color-text-2)",
              fontSize: "0.72rem",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            <th style={{ ...thStyle, width: "26%" }}>{IMPORT_COPY.colFile}</th>
            <th style={{ ...thStyle, width: "20%" }}>{IMPORT_COPY.colClient}</th>
            <th style={{ ...thStyle, width: "16%" }}>
              {IMPORT_COPY.colYear} / {IMPORT_COPY.colPeriod}
            </th>
            <th style={{ ...thStyle, width: "22%" }}>
              {IMPORT_COPY.colFramework} / {IMPORT_COPY.colPackage}
            </th>
            <th style={{ ...thStyle, width: 160 }}>{IMPORT_COPY.colStatus}</th>
            <th style={{ ...thStyle, width: 36 }} aria-label={IMPORT_COPY.rowActionsHeader} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} style={{ borderTop: "1px solid var(--color-border)" }}>
              <td style={tdStyle}>
                <FileCell row={row} onRetryUpload={props.onRetryUpload} onPatch={props.onPatch} />
              </td>
              <td style={tdStyle}>
                <ClientCell
                  row={row}
                  clients={props.clients}
                  onPatch={props.onPatch}
                  onClientKnown={props.onClientKnown}
                  canCreateClient={props.canCreateClient}
                  onCreateClient={props.onCreateClient}
                />
              </td>
              <td style={tdStyle}>
                <PeriodCell row={row} frameworks={props.frameworks} onPatch={props.onPatch} />
              </td>
              <td style={tdStyle}>
                <FrameworkCell
                  row={row}
                  clients={props.clients}
                  frameworks={props.frameworks}
                  packagesByVersion={props.packagesByVersion}
                  onPatch={props.onPatch}
                />
              </td>
              <td style={tdStyle}>
                <StatusCell row={row} existing={props.existing} />
              </td>
              <td style={tdStyle}>
                <RemoveButton row={row} onRemove={props.onRemove} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 10px", fontWeight: 600 };
const tdStyle: React.CSSProperties = {
  padding: "10px 10px",
  verticalAlign: "top",
  color: "var(--color-text)",
};

// ---------------------------------------------------------------------------
// Hücreler — tablo satırı ve kart düzeni AYNI bileşenleri kullanır (tek kaynak).
// ---------------------------------------------------------------------------

function Line2({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "center",
        marginTop: 4,
        fontSize: SMALL,
        color: "var(--color-text-2)",
      }}
    >
      {children}
    </div>
  );
}

function FileCell({
  row,
  onRetryUpload,
  onPatch,
}: {
  row: ImportRowState;
  onRetryUpload: (id: string) => void;
  onPatch: ImportRowsTableProps["onPatch"];
}): JSX.Element {
  const primaryName = row.fileNames[0] ?? "—";
  const pairNote = row.files.length > 1 ? ` · + .docx (${IMPORT_COPY.pairNote})` : "";
  return (
    <div style={{ minWidth: 0 }}>
      <span
        title={row.fileNames.join(" · ")}
        style={{ display: "block", fontWeight: 500, overflow: "hidden" }}
      >
        {middleEllipsis(primaryName)}
      </span>
      <Line2>
        {row.upload.phase === "uploading" ? (
          <span
            role="progressbar"
            aria-valuenow={row.upload.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-busy="true"
            aria-label={fillTemplate(IMPORT_COPY.rowUploading, { n: row.upload.percent })}
          >
            {fillTemplate(IMPORT_COPY.rowUploading, { n: row.upload.percent })}
          </span>
        ) : row.upload.phase === "suggesting" ? (
          <span role="status">{IMPORT_COPY.rowSuggesting}</span>
        ) : row.upload.phase === "failed" ? (
          <span>
            <span aria-hidden="true" style={{ color: "var(--color-error)" }}>
              ✕{" "}
            </span>
            {/* `uploadFailed` = "{file} yüklenemedi — Yeniden dene"; son parça düğme olur. */}
            {fillTemplate(IMPORT_COPY.uploadFailed, { file: primaryName }).split(" — ")[0]}{" "}
            <button type="button" onClick={() => onRetryUpload(row.id)} style={linkBtnStyle}>
              {IMPORT_COPY.uploadFailed.split(" — ")[1]}
            </button>
          </span>
        ) : (
          <span>
            {row.pageCount !== null ? `${row.pageCount.toString()} s.` : IMPORT_COPY.fileUploaded}
            {pairNote}
          </span>
        )}
        <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          {IMPORT_COPY.colType.toLocaleLowerCase("tr-TR")}:
          {/* Sabit liste (UX paketi): serbest metinde kapak başlığı olduğu gibi
              yazılabiliyordu. Gövdeye ETİKET gider — bkz. `reduceRowsForBatch`. */}
          <select
            value={row.reportType ?? ""}
            aria-label={IMPORT_COPY.colType}
            onChange={(e) =>
              onPatch(
                row.id,
                { reportType: (e.target.value || null) as ReportType | null },
                "reportType",
              )
            }
            style={{ ...inputStyle, width: 150 }}
          >
            <option value="">— seçin —</option>
            {REPORT_TYPES.map((t) => (
              <option key={t} value={t}>
                {REPORT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
          {row.suggested.includes("reportType") ? <Star /> : null}
        </label>
      </Line2>
    </div>
  );
}

/**
 * Picker listesinin sonundaki "+ Yeni müşteri" ek seçeneğinin anahtarı. Seçilince
 * `clientId`'ye YAZILMAZ, satır içi editörü açar (picker değeri `row.clientId`'dir).
 */
const NEW_CLIENT_VALUE = "__new_client__";

/**
 * Satırın ADAY havuzu (ClientPicker işi): kapak adının / yazılan adın marka parçasıyla
 * sunucu araması (debounce'lu, yarış korumalı). `similarClientOptions` / `exactClientMatch`
 * yalnız bu havuza bakar — 500'lük toplu liste yok. Boş terimde boş havuz.
 */
function useClientCandidates(name: string | null): ClientOption[] {
  const term = clientSearchTerm(name);
  const [pool, setPool] = useState<{ term: string; items: ClientOption[] }>({
    term: "",
    items: [],
  });
  const seq = useRef(0);
  useEffect(() => {
    if (!term) return;
    const my = (seq.current += 1);
    const t = window.setTimeout(() => {
      searchClients(term, 20)
        .then((res) => {
          if (my !== seq.current) return;
          setPool({
            term,
            items: res.items.map((c) => ({ id: c.id, name: c.name, sector: c.sector ?? null })),
          });
        })
        .catch(() => {
          if (my === seq.current) setPool({ term, items: [] });
        });
    }, PICKER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [term]);
  return term && pool.term === term ? pool.items : [];
}

function ClientCell({
  row,
  clients,
  onPatch,
  onClientKnown,
  canCreateClient,
  onCreateClient,
}: {
  row: ImportRowState;
  clients: ClientOption[];
  onPatch: ImportRowsTableProps["onPatch"];
  onClientKnown: ImportRowsTableProps["onClientKnown"];
  canCreateClient: boolean;
  onCreateClient: ImportRowsTableProps["onCreateClient"];
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  // Kapak önerisinin sektörüyle ön-dolu; kullanıcı sözlükten değiştirebilir ya da boşaltabilir.
  const [sector, setSector] = useState<SectorCode | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Çift tık koruması state DEĞİL ref ile (FormManager "Ekle" emsali — state
  // güncellemesi aynı tık dalgasında henüz görünmez olabilir, iki POST giderdi).
  const inFlight = useRef(false);
  const pickerId = `import-client-${row.id}`;

  // Kapak adına benzeyen mevcut kayıtlar → picker'ın boş sorgudaki "Öneriler" bölümü
  // (yalnız müşteri henüz seçilmemişken aranır).
  const coverPool = useClientCandidates(row.clientId === null ? row.suggestedClientName : null);
  const suggestions = useMemo(
    () =>
      row.clientId === null && row.suggestedClientName
        ? similarClientOptions(row.suggestedClientName, coverPool, 3).map((c) => ({
            id: c.id,
            name: c.name,
            sector: c.sector,
            ...(c.sector ? { secondary: c.sector } : {}),
          }))
        : [],
    [row.clientId, row.suggestedClientName, coverPool],
  );
  // Editörde yazılan ada göre aday havuzu — mükerrer kayıt açmanın tek savunması
  // (API'de ad tekilliği yok); havuz boşken kapak havuzu yedek.
  const typedPool = useClientCandidates(editing ? name : null);
  const pool = typedPool.length > 0 ? typedPool : coverPool;
  const similar = useMemo(
    () => (editing ? similarClientOptions(name, pool) : []),
    [editing, name, pool],
  );
  // Normalize halde AYNI kayıt varsa Oluştur kapanır; kullanıcı mevcut kaydı seçer.
  const duplicate = useMemo(
    () => (editing ? exactClientMatch(name, pool) : null),
    [editing, name, pool],
  );

  const selectedClient = clients.find((c) => c.id === row.clientId) ?? null;
  const value = row.clientId
    ? {
        id: row.clientId,
        name: selectedClient?.name ?? "…",
        sector: selectedClient?.sector ?? null,
      }
    : null;

  function closeEditor(focusSelect = true): void {
    setEditing(false);
    setError(null);
    // Odaklı input unmount olunca odak body'ye düşerdi — klavye kullanıcısı satırı
    // kaybetmesin diye müşteri picker'ına döner (a11y, inceleme bulgusu).
    if (focusSelect) document.getElementById(pickerId)?.focus();
  }

  function openEditor(): void {
    setName(row.suggestedClientName ?? "");
    setSector(row.suggestedSector ?? "");
    setError(null);
    setEditing(true);
  }

  // Başka bir satırda aynı firma yaratıldıysa `propagateClientSelection` bu satırın
  // müşterisini doldurur — AÇIK editör kapanmalı, aksi halde kullanıcı aynı firma
  // için ikinci kaydı açabilirdi (brief §1'in amacı).
  useEffect(() => {
    if (row.clientId !== null) {
      setEditing(false);
      setError(null);
    }
  }, [row.clientId]);

  function pickClient(clientId: string): void {
    const known = pool.find((c) => c.id === clientId);
    if (known) onClientKnown(known);
    onPatch(row.id, { clientId }, "clientId");
    closeEditor();
  }

  async function create(): Promise<void> {
    if (inFlight.current) return;
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setError(IMPORT_COPY.newClientTooShort);
      return;
    }
    if (duplicate) {
      setError(IMPORT_COPY.newClientExists);
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const res = await onCreateClient(
        row.id,
        trimmed,
        sector === "" ? null : SECTOR_LABELS[sector],
      );
      // Başarısızlıkta editör AÇIK kalır: "Benzer kayıtlar" satırı duruyor, kullanıcı
      // mevcut kaydı seçebilir (ad çakışması dönen bir API'nin karşılığı da budur).
      if (res.ok) closeEditor();
      else setError(res.message ?? IMPORT_COPY.newClientError);
    } catch {
      // Beklenmeyen gövde/istisna da editörü kilitli bırakmaz (finally).
      setError(IMPORT_COPY.newClientError);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <div style={{ minWidth: 0 }}>
      <ClientPicker
        id={pickerId}
        size="sm"
        ariaLabel={IMPORT_COPY.colClient}
        placeholder="— seçin —"
        value={value}
        suggestions={suggestions}
        showRecent={false}
        allLink={null}
        onChange={(v) => {
          if (v) onClientKnown({ id: v.id, name: v.name, sector: v.sector ?? null });
          onPatch(row.id, { clientId: v?.id ?? null }, "clientId");
        }}
        extraOptions={
          canCreateClient
            ? [
                {
                  key: NEW_CLIENT_VALUE,
                  label: row.suggestedClientName
                    ? fillTemplate(IMPORT_COPY.newClientOption, { name: row.suggestedClientName })
                    : IMPORT_COPY.newClientOptionBlank,
                },
              ]
            : []
        }
        onExtra={(key) => {
          if (key === NEW_CLIENT_VALUE) openEditor();
        }}
      />

      {editing ? (
        <div
          style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}
          onKeyDown={(e) => {
            // Esc editörün HER yerinde çalışır (Oluştur/Vazgeç odaklıyken de).
            if (e.key === "Escape") {
              e.preventDefault();
              closeEditor();
            }
          }}
        >
          <input
            type="text"
            autoFocus
            value={name}
            disabled={busy}
            aria-label={IMPORT_COPY.newClientNameLabel}
            placeholder={IMPORT_COPY.newClientNameLabel}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void create();
              }
            }}
            style={{ ...inputStyle, width: "100%" }}
          />
          <select
            value={sector}
            disabled={busy}
            aria-label={IMPORT_COPY.newClientSectorLabel}
            onChange={(e) => setSector(e.target.value as SectorCode | "")}
            style={{ ...inputStyle, width: "100%" }}
          >
            <option value="">{IMPORT_COPY.newClientSectorBlank}</option>
            {SECTOR_CODES.map((c) => (
              <option key={c} value={c}>
                {SECTOR_LABELS[c]}
              </option>
            ))}
          </select>
          {duplicate ? (
            <div style={{ fontSize: SMALL, color: "var(--color-warning-text)" }}>
              {IMPORT_COPY.newClientExists}{" "}
              <button type="button" style={linkBtnStyle} onClick={() => pickClient(duplicate.id)}>
                {duplicate.name}
              </button>
            </div>
          ) : similar.length > 0 ? (
            <div style={{ fontSize: SMALL, color: "var(--color-text-2)" }}>
              {IMPORT_COPY.newClientSimilar}{" "}
              {similar.map((c, i) => (
                <span key={c.id}>
                  {i > 0 ? " · " : ""}
                  <button type="button" style={linkBtnStyle} onClick={() => pickClient(c.id)}>
                    {c.name}
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          {error ? (
            <div role="alert" style={{ fontSize: SMALL, color: "var(--color-error)" }}>
              {error}
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              disabled={busy || duplicate !== null}
              aria-busy={busy || undefined}
              onClick={() => void create()}
              style={cellBtnStyle(true)}
            >
              {IMPORT_COPY.newClientCreate}
            </button>
            <button type="button" onClick={() => closeEditor()} style={cellBtnStyle(false)}>
              {IMPORT_COPY.cancelGeneric}
            </button>
          </div>
        </div>
      ) : null}

      {row.suggested.includes("clientId") ? (
        <Line2>
          <Star /> {IMPORT_COPY.suggestionMark.replace("✦ ", "")}
        </Line2>
      ) : null}
    </div>
  );
}

function cellBtnStyle(primary: boolean): React.CSSProperties {
  return {
    padding: "3px 10px",
    borderRadius: 6,
    fontSize: SMALL,
    fontWeight: 600,
    cursor: "pointer",
    border: primary ? "none" : "1px solid var(--color-border)",
    background: primary ? "var(--color-primary)" : "var(--color-surface)",
    color: primary ? "var(--color-surface)" : "var(--color-text-2)",
  };
}

function PeriodCell({
  row,
  frameworks,
  onPatch,
}: {
  row: ImportRowState;
  frameworks: PublishedFrameworkEntry[];
  onPatch: ImportRowsTableProps["onPatch"];
}): JSX.Element {
  const fw = frameworks.find((f) => f.version.id === row.frameworkVersionId) ?? null;
  const quarterly = fw?.supportsQuarterly === true;
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      <input
        type="number"
        min={2000}
        max={2100}
        aria-label={IMPORT_COPY.colYear}
        value={row.reportingYear ?? ""}
        onChange={(e) =>
          onPatch(
            row.id,
            { reportingYear: e.target.value ? Number(e.target.value) : null },
            "reportingYear",
          )
        }
        style={{ ...inputStyle, width: 72 }}
      />
      {row.suggested.includes("reportingYear") ? <Star /> : null}
      {quarterly ? (
        <select
          aria-label={IMPORT_COPY.colPeriod}
          value={row.periodType}
          onChange={(e) =>
            onPatch(row.id, { periodType: e.target.value as PeriodType }, "periodType")
          }
          style={{ ...inputStyle, width: 96 }}
        >
          {PERIOD_TYPES.map((p) => (
            <option key={p} value={p}>
              {PERIOD_LABELS[p]}
            </option>
          ))}
        </select>
      ) : (
        <span style={{ fontSize: SMALL, color: "var(--color-text-2)" }}>
          {PERIOD_LABELS.annual}
        </span>
      )}
      {row.suggested.includes("periodType") && quarterly ? <Star /> : null}
    </div>
  );
}

function FrameworkCell({
  row,
  clients,
  frameworks,
  packagesByVersion,
  onPatch,
}: {
  row: ImportRowState;
  clients: ClientOption[];
  frameworks: PublishedFrameworkEntry[];
  packagesByVersion: Record<string, SectorPackageSummary[]>;
  onPatch: ImportRowsTableProps["onPatch"];
}): JSX.Element {
  const packages = row.frameworkVersionId ? (packagesByVersion[row.frameworkVersionId] ?? []) : [];
  // P15/6 — paket müşteri sektörüne göre değil RAPORA göre önerildiyse satır içi not.
  const mismatch = sectorMismatchNote(
    matchSectorPackage(row, clients.find((c) => c.id === row.clientId) ?? null, packages),
  );
  const selected = frameworks.find((f) => f.version.id === row.frameworkVersionId) ?? null;
  return (
    <div style={{ minWidth: 0 }}>
      <select
        aria-label={IMPORT_COPY.colFramework}
        value={row.frameworkVersionId ?? ""}
        title={selected ? `${selected.frameworkName} ${selected.version.versionNumber}` : ""}
        onChange={(e) => {
          const versionId = e.target.value || null;
          const fw = frameworks.find((f) => f.version.id === versionId) ?? null;
          onPatch(
            row.id,
            {
              frameworkVersionId: versionId,
              frameworkId: fw?.frameworkId ?? null,
              sectorPackageId: null,
              // Çerçeve çeyrekliği desteklemiyorsa dönem yıllığa döner (ProjectNew 1C ikizi).
              ...(fw?.supportsQuarterly === true ? {} : { periodType: "annual" }),
            },
            "frameworkVersionId",
          );
        }}
        style={{ ...inputStyle, width: "100%" }}
      >
        <option value="">— seçin —</option>
        {frameworks.map((f) => (
          <option key={f.version.id} value={f.version.id}>
            {f.frameworkName} {f.version.versionNumber}
          </option>
        ))}
      </select>
      {packages.length > 0 ? (
        <Line2>
          <select
            aria-label={IMPORT_COPY.colPackage}
            value={row.sectorPackageId ?? ""}
            onChange={(e) =>
              onPatch(row.id, { sectorPackageId: e.target.value || null }, "sectorPackageId")
            }
            style={{ ...inputStyle, width: "100%" }}
          >
            <option value="">— paket yok —</option>
            {packages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {row.suggested.includes("sectorPackageId") ? <Star /> : null}
        </Line2>
      ) : null}
      {mismatch !== null && row.suggested.includes("sectorPackageId") ? (
        <div style={{ fontSize: SMALL, color: "var(--color-warning-text)", marginTop: 2 }}>
          {mismatch}
        </div>
      ) : null}
    </div>
  );
}

function StatusCell({
  row,
  existing,
  hidePill = false,
}: {
  row: ImportRowState;
  existing: readonly ExistingImportKey[];
  /** Kart düzeninde pill başlıkta duruyor — gövdede tekrarlanmaz (1A). */
  hidePill?: boolean;
}): JSX.Element {
  const readiness = rowReadinessOf(row, existing);
  const missing = readiness === "eksik" ? missingNote(row) : null;
  return (
    <div>
      {hidePill ? null : <ReadinessPill row={row} existing={existing} />}
      {missing ? <Line2>{missing}</Line2> : null}
      {readiness === "zaten var" && row.existingProjectId ? (
        <Line2>
          {/* `Link` — ham `<a>` tam sayfa gezinme yapıp `beforeunload` uyarısını
              tetikliyordu (7A taslağı hâlâ duruyorken, fix round 1). */}
          <Link
            to={`/projects/${row.existingProjectId}`}
            style={{
              color: "var(--color-primary)",
              textDecoration: "underline",
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
          >
            {IMPORT_COPY.openExistingProject}
          </Link>
        </Line2>
      ) : null}
      {readiness === "zaten var" && !row.existingProjectId ? (
        <Line2>{IMPORT_COPY.existsActiveImport}</Line2>
      ) : null}
    </div>
  );
}

function RemoveButton({
  row,
  onRemove,
}: {
  row: ImportRowState;
  onRemove: (id: string) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onRemove(row.id)}
      aria-label={fillTemplate(IMPORT_COPY.removeRow, { fileName: row.fileNames[0] ?? "" })}
      title={fillTemplate(IMPORT_COPY.removeRow, { fileName: row.fileNames[0] ?? "" })}
      style={{
        width: 24,
        height: 24,
        borderRadius: 6,
        border: "1px solid transparent",
        background: "transparent",
        color: "var(--color-text-3)",
        cursor: "pointer",
        lineHeight: 1,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-error)")}
      onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-text-3)")}
    >
      ✕
    </button>
  );
}

const linkBtnStyle: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-primary)",
  textDecoration: "underline",
  fontWeight: 600,
  fontSize: SMALL,
  cursor: "pointer",
  padding: 0,
};

/** ≤ 1100 px kart düzeni (1A): başlıkta dosya + durum pill, gövde 2 sütun grid. */
function RowCard({ row, ...p }: { row: ImportRowState } & ImportRowsTableProps): JSX.Element {
  return (
    <div
      className="rounded-lg"
      style={{ border: "1px solid var(--color-border)", padding: "10px 12px" }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <FileCell row={row} onRetryUpload={p.onRetryUpload} onPatch={p.onPatch} />
        </div>
        <ReadinessPill row={row} existing={p.existing} />
        <RemoveButton row={row} onRemove={p.onRemove} />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: "8px 12px",
          marginTop: 10,
          fontSize: SMALL,
          color: "var(--color-text-2)",
        }}
      >
        {/* Alanların kendi `aria-label`'ı var — kart başlıkları görsel etiket olduğu için
            <label> ile sarılmaz (tek <label> birden çok kontrolü işaretleyemez). */}
        <div>
          {IMPORT_COPY.colClient}
          <ClientCell
            row={row}
            clients={p.clients}
            onPatch={p.onPatch}
            onClientKnown={p.onClientKnown}
            canCreateClient={p.canCreateClient}
            onCreateClient={p.onCreateClient}
          />
        </div>
        <div>
          {IMPORT_COPY.colYear} / {IMPORT_COPY.colPeriod}
          <PeriodCell row={row} frameworks={p.frameworks} onPatch={p.onPatch} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          {IMPORT_COPY.colFramework} / {IMPORT_COPY.colPackage}
          <FrameworkCell
            row={row}
            clients={p.clients}
            frameworks={p.frameworks}
            packagesByVersion={p.packagesByVersion}
            onPatch={p.onPatch}
          />
        </div>
      </div>
      <StatusCell row={row} existing={p.existing} hidePill />
    </div>
  );
}
